import {
  notificationAddAction,
  notificationDismissAction,
  UseFormTools,
} from "@wisr/common";
import { CalculatorFormValues, Serviceability } from "./calculator";
import { DebtType } from "./calculator.consts";
import { RepaymentFrequency } from "../../application/form/application-form.consts";
import { formatDateString } from "../../application/form/application-form.utils";
import { CalculateServiceabilityRequest } from "../../types/calculate-serviceability";
import {
  existingOtherIncomeCount,
  existingSecondApplicantOtherIncomeCount,
} from "./sections/income-section/income-section.utils";
import { existingLiabilitiesCount } from "./sections/liabilities-section/liabilities-section.utils";
import { Dispatch } from "redux";
import { FormNotification } from "../../application/form/application";

const CALCULATOR_STORAGE_KEY = "serviceabilityCalculatorInputs";

export const getStoredCalculatorInputs = (): Partial<Serviceability> | null => {
  try {
    const stored = sessionStorage.getItem(CALCULATOR_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const saveCalculatorInputs = (formData: Serviceability) => {
  try {
    sessionStorage.setItem(CALCULATOR_STORAGE_KEY, JSON.stringify(formData));
    return true;
  } catch {
    return false;
  }
};

export const clearCalculatorInputs = () => {
  try {
    sessionStorage.removeItem(CALCULATOR_STORAGE_KEY);
  } catch {
    return;
  }
};

export const displayNotification =
  (dispatch: Dispatch) => (notification: FormNotification) => {
    dispatch(
      notificationAddAction({
        message: notification.message,
        type: notification.type,
        details: notification.details,
        close: /* istanbul ignore next */ (n) => () =>
          dispatch(notificationDismissAction(n.id)),
      })
    );
  };

type FinancialDetails =
  CalculateServiceabilityRequest["mainApplicant"]["financialDetails"];
type Income = FinancialDetails["income"][number];

const getResidencyStatus = (
  livingSituation: string,
  residencies: CalculatorFormValues["residencies"]
) =>
  residencies.find((residency) => residency.value === livingSituation)?.label ??
  livingSituation;

const getMonthlyRepayment = (
  repayment: number,
  frequency: string | null | undefined
) => {
  switch (frequency) {
    case RepaymentFrequency.MONTHLY:
      return repayment;
    case RepaymentFrequency.WEEKLY:
      return repayment * 4.333;
    case RepaymentFrequency.FORTNIGHTLY:
      return (repayment / 2) * 4.333;
    case RepaymentFrequency.ANNUAL:
      return repayment / 12;
    default:
      return repayment;
  }
};

export const mapToCalculateServiceabilityRequest = (
  formData: UseFormTools<Serviceability>["formData"],
  residencies: CalculatorFormValues["residencies"]
): CalculateServiceabilityRequest => {
  const isJoint = formData.applicationType === "Joint";
  const liabilitiesLength = existingLiabilitiesCount(formData);
  const otherIncomeLength = existingOtherIncomeCount(formData);
  const secondApplicantOtherIncomeLength =
    existingSecondApplicantOtherIncomeCount(formData);

  const isMortgage = (index: number) =>
    formData[`financialDebtType_${index}`] === DebtType.MORTGAGE;
  const isPersonalLoan = (index: number) =>
    formData[`financialDebtType_${index}`] === DebtType.PERSONAL_LOAN;
  const isCreditCard = (index: number) =>
    formData[`financialDebtType_${index}`] === DebtType.CREDIT_CARD;
  const isJointLiability = (index: number) =>
    isJoint || formData[`financialDebtIsJointLoan_${index}`] === "Yes";
  const isInvestment = (index: number) =>
    formData[`financialDebtIsInvestment_${index}`] === "Yes";
  const isConsolidated = (index: number) =>
    formData[`financialDebtConsolidate_${index}`] === "Yes";

  let mortgageAmount = 0;
  let mortgageAmountJoint = 0;
  let investmentMortgageAmount = 0;
  let investmentMortgageAmountJoint = 0;
  let otherLoanAmount = 0;
  let otherLoanAmountJoint = 0;
  let creditCardTotalLimit = 0;
  const commitments: FinancialDetails["commitments"] = [];

  for (let i = 1; i <= liabilitiesLength; i++) {
    const repayment = +formData[`financialDebtRepaymentAmount_${i}`] || 0;
    const frequency = formData[`financialDebtRepaymentFrequency_${i}`];
    const monthlyRepayment = getMonthlyRepayment(repayment, frequency);

    if (isMortgage(i)) {
      if (isInvestment(i)) {
        if (isJointLiability(i)) {
          investmentMortgageAmountJoint += monthlyRepayment;
        } else {
          investmentMortgageAmount += monthlyRepayment;
        }
      } else if (isJointLiability(i)) {
        mortgageAmountJoint += monthlyRepayment;
      } else {
        mortgageAmount += monthlyRepayment;
      }
    } else if (isPersonalLoan(i)) {
      if (isJointLiability(i)) {
        otherLoanAmountJoint += monthlyRepayment;
      } else {
        otherLoanAmount += monthlyRepayment;
      }

      if (isConsolidated(i)) {
        commitments.push({
          reduceCommitment: true,
          description: "Personal Loan",
          financialInstitution: "",
          closeAmount: 0,
        });
      }
    } else if (isCreditCard(i)) {
      creditCardTotalLimit +=
        +formData[`financialDebtCreditCardLimit_${i}`] || 0;
    }
  }

  const ownsInvestmentProperty = Array.from(
    { length: liabilitiesLength },
    (_, i) => i + 1
  ).some((i) => isMortgage(i) && isInvestment(i));

  const income: Income[] = [];

  if (+formData.primaryIncomeAmount > 0) {
    income.push({
      amount: getMonthlyRepayment(
        +formData.primaryIncomeAmount,
        formData.primaryIncomeFrequency
      ),
      description: formData.primaryIncomeSource,
      frequency: RepaymentFrequency.MONTHLY,
      isPrimaryIncome: true,
      isPartnerIncome: false,
    });
  }

  if (!isJoint && +formData.partnerIncome > 0) {
    income.push({
      amount: +formData.partnerIncome,
      description: "Partner Income",
      frequency: RepaymentFrequency.ANNUAL,
      isPrimaryIncome: false,
      isPartnerIncome: true,
    });
  }

  for (let i = 1; i <= otherIncomeLength; i++) {
    const amount = +formData[`otherIncomeAmount_${i}`] || 0;
    if (amount === 0) continue;

    income.push({
      amount: getMonthlyRepayment(
        amount,
        formData[`otherIncomeFrequency_${i}`]
      ),
      description: formData[`otherIncomeSource_${i}`],
      frequency: RepaymentFrequency.MONTHLY,
      isPrimaryIncome: false,
      isPartnerIncome: false,
    });
  }

  const additionalLivingExpense =
    +formData.healthInsurance +
    +formData.privateEducation +
    +formData.landTax +
    +formData.childSupport +
    +formData.otherALE;

  const secondApplicantAdditionalLivingExpense =
    +formData.secondApplicantHealthInsurance +
    +formData.secondApplicantPrivateEducation +
    +formData.secondApplicantLandTax +
    +formData.secondApplicantChildSupport +
    +formData.secondApplicantOtherALE;

  const mainApplicant: CalculateServiceabilityRequest["mainApplicant"] = {
    wisrCustomerId: "00000000-0000-0000-0000-000000000000",
    loanId: 0,
    parentId: "",
    personalDetails: {
      maritalStatus: isJoint ? "Married" : formData.maritalStatus,
      totalDependentsNoSpouse: +formData.numberOfDependents,
      postcode: formData.postcode,
      state: formData.state.toUpperCase(),
    },
    financialDetails: {
      liabilities: {
        totalLiabilities: 0,
      },
      assets: {
        totalAssets: 0,
      },
      mortgage: {
        amount: mortgageAmount,
        frequency: RepaymentFrequency.MONTHLY,
        mortgageAmountJoint,
        mortgageJointFrequency: RepaymentFrequency.MONTHLY,
      },
      investmentMortgage: {
        amount: investmentMortgageAmount,
        frequency: RepaymentFrequency.MONTHLY,
        mortgageAmountJoint: investmentMortgageAmountJoint,
        mortgageJointFrequency: RepaymentFrequency.MONTHLY,
      },
      rent: {
        amount: +formData.totalRentAmount,
        frequency: formData.rentAmountFrequency,
        isShared: formData.rentShared === "Yes",
      },
      otherLoan: {
        amount: otherLoanAmount,
        frequency: RepaymentFrequency.MONTHLY,
        otherLoanAmountJoint,
        otherLoanJointFrequency: RepaymentFrequency.MONTHLY,
      },
      generalLivingExpense: {
        amount: +formData.totalDeclaredGLE,
        frequency: RepaymentFrequency.MONTHLY,
      },
      additionalLivingExpense: {
        amount: additionalLivingExpense,
        frequency: RepaymentFrequency.MONTHLY,
      },
      creditCard: {
        totalLimit: creditCardTotalLimit,
      },
      income,
      commitments,
      personalLoanBalance: 0,
    },
  };

  let secondaryApplicant: CalculateServiceabilityRequest["secondaryApplicant"];

  if (isJoint) {
    const secondApplicantIncome: Income[] = [];

    if (+formData.secondApplicantPrimaryIncomeAmount > 0) {
      secondApplicantIncome.push({
        amount: getMonthlyRepayment(
          +formData.secondApplicantPrimaryIncomeAmount,
          formData.secondApplicantPrimaryIncomeFrequency
        ),
        description: formData.secondApplicantPrimaryIncomeSource,
        frequency: RepaymentFrequency.MONTHLY,
        isPrimaryIncome: true,
        isPartnerIncome: false,
      });
    }

    for (let i = 1; i <= secondApplicantOtherIncomeLength; i++) {
      const amount = +formData[`secondApplicantOtherIncomeAmount_${i}`] || 0;
      if (amount === 0) continue;

      secondApplicantIncome.push({
        amount: getMonthlyRepayment(
          amount,
          formData[`secondApplicantOtherIncomeFrequency_${i}`]
        ),
        description: formData[`secondApplicantOtherIncomeSource_${i}`],
        frequency: RepaymentFrequency.MONTHLY,
        isPrimaryIncome: false,
        isPartnerIncome: false,
      });
    }

    secondaryApplicant = {
      wisrCustomerId: "00000000-0000-0000-0000-000000000000",
      loanId: 0,
      parentId: "",
      personalDetails: {
        maritalStatus: "Married",
        totalDependentsNoSpouse: +formData.numberOfDependents,
        postcode: formData.postcode,
        state: formData.state.toUpperCase(),
      },
      financialDetails: {
        liabilities: {
          totalLiabilities: 0,
        },
        assets: {
          totalAssets: 0,
        },
        mortgage: {
          amount: 0,
          frequency: RepaymentFrequency.MONTHLY,
          mortgageAmountJoint: 0,
          mortgageJointFrequency: RepaymentFrequency.MONTHLY,
        },
        investmentMortgage: {
          amount: 0,
          frequency: RepaymentFrequency.MONTHLY,
          mortgageAmountJoint: 0,
          mortgageJointFrequency: RepaymentFrequency.MONTHLY,
        },
        rent: {
          amount: 0,
          frequency: RepaymentFrequency.MONTHLY,
          isShared: false,
        },
        otherLoan: {
          amount: 0,
          frequency: RepaymentFrequency.MONTHLY,
          otherLoanAmountJoint: 0,
          otherLoanJointFrequency: RepaymentFrequency.MONTHLY,
        },
        generalLivingExpense: {
          amount: +formData.totalDeclaredGLE,
          frequency: RepaymentFrequency.MONTHLY,
        },
        additionalLivingExpense: {
          amount: secondApplicantAdditionalLivingExpense,
          frequency: RepaymentFrequency.MONTHLY,
        },
        creditCard: {
          totalLimit: 0,
        },
        income: secondApplicantIncome,
        commitments: [],
        personalLoanBalance: 0,
      },
    };
  }

  return {
    loanDetails: {
      interestRate: +formData.interestRate,
      loanTerm: +formData.loanTerm,
      loanAmount: +formData.loanAmount,
      applicationFee: +formData.establishmentFee + +formData.brokerFee,
      monthlyAccountFee: +formData.monthlyFee || 0,
      repaymentBasis: "",
      wisrPartner: {
        commissionAmount: 0,
      },
    },
    mainApplicant,
    ...(secondaryApplicant && { secondaryApplicant }),
    isSecured: formData.loanProductType !== "Unsecured",
    residencyStatus: getResidencyStatus(formData.livingSituation, residencies),
    dob: formatDateString("01/01/1990"),
    serviceabilityMethod: "CashSurplus",
    serviceabilityEmployment: {
      employmentStatus: "full-time",
      monthsAtEmployer: 200,
      employmentIndustry: "finance-insurance",
    },
    ownsInvestmentProperty,
    ...(formData.sessionId && { sessionId: formData.sessionId }),
    ...(formData.name && { name: formData.name }),
  };
};
